/**
 * 대시보드 — 홈 화면
 * ---------------------------------------------------------------------------
 * 들어오자마자 두 가지를 본다.
 *
 *   달력   이번 달에 어느 날을 썼고 어느 날이 빠졌는가. 빠진 날을 누르면
 *          그 날짜로 바로 작성 화면을 연다.
 *   차트   가동시간·사용량·처리효율의 추이.
 *
 * 차트 켜고 끄기
 *   기업마다 쓰는 일지가 다르다(대기만, 폐수 위탁만, 둘을 함께). 쓰지 않는
 *   서식의 차트와 "미작성" 표시가 매일 뜨면 대시보드는 소음이 된다. 켜고 끄는
 *   것은 **기업 설정**이다 — 한 사람이 끄면 그 기업 모두의 대시보드에서
 *   사라지므로 사업장 관리 권한이 있는 사람만 바꾼다.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntApp,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type {
  CalendarDay,
  CalendarMonth,
  DashboardCharts,
  DashboardSetting,
  LogType,
  Site,
} from '@eco/shared';
import { ECO_PERMISSIONS, LOG_TYPES, LOG_TYPE_MENU } from '@eco/shared';
import { api, errorMessage } from '../lib/api';
import { siteOptions } from '../lib/siteOptions';
import { can, useSession } from '../lib/session';
import { LOG_TYPE_COLOR, LOG_TYPE_ICON } from '../components/logTypeVisual';
import { HelpH, HelpList, HelpP, PageHelp } from '../components/PageHelp';
import { AiReportButton } from '../components/AiReportDialog';
import { LogCalendar } from './dashboard/LogCalendar';
import { DayDialog } from './dashboard/DayDialog';
import { ChartGrid } from './dashboard/ChartPanel';

const { Text, Title } = Typography;

type Period = 'month' | '30d' | '90d' | 'custom';

export function DashboardPage() {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const canManage = can(session, ECO_PERMISSIONS.SITE_MANAGE);
  const canWrite = can(session, ECO_PERMISSIONS.DAILY_LOG_WRITE);

  const [siteId, setSiteIdState] = useState<string | undefined>(() => readLastSite());

  /** 고른 사업장을 기억한다. 다음에 들어와도 같은 사업장을 본다. */
  const setSiteId = (next: string) => {
    setSiteIdState(next);
    writeLastSite(next);
  };
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  const [period, setPeriod] = useState<Period>('30d');
  const [custom, setCustom] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, 'day'), dayjs()]);

  const sites = useQuery<Site[]>({
    queryKey: ['sites'],
    queryFn: async () => (await api.get<Site[]>('/sites')).data,
  });

  /*
    사업장을 미리 고른다. 대부분 한 곳이고, 고르지 않으면 미작성을 판정할 수 없다.

    기억해 둔 사업장이 목록에 없으면(지워졌거나 다른 기업으로 들어왔을 때)
    첫 번째로 돌아간다. 없는 사업장을 붙들고 있으면 대시보드가 통째로 빈다.
  */
  useEffect(() => {
    if (!sites.data) return;
    const known = sites.data.some((s) => s.siteId === siteId);
    if (!known && sites.data[0]) setSiteIdState(sites.data[0].siteId);
  }, [sites.data, siteId]);

  const setting = useQuery<DashboardSetting>({
    queryKey: ['dashboard-setting'],
    queryFn: async () => (await api.get<DashboardSetting>('/dashboard/settings')).data,
  });

  const calendar = useQuery<CalendarMonth>({
    queryKey: ['dashboard-calendar', siteId, month.year(), month.month() + 1],
    queryFn: async () =>
      (
        await api.get<CalendarMonth>('/dashboard/calendar', {
          params: { siteId, year: month.year(), month: month.month() + 1 },
        })
      ).data,
    enabled: !!siteId,
  });

  const range = useMemo<[string, string]>(() => {
    const today = dayjs();
    switch (period) {
      case 'month':
        return [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
      case '90d':
        return [today.subtract(89, 'day').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
      case 'custom':
        return [custom[0].format('YYYY-MM-DD'), custom[1].format('YYYY-MM-DD')];
      default:
        return [today.subtract(29, 'day').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    }
  }, [period, custom]);

  const charts = useQuery<DashboardCharts>({
    queryKey: ['dashboard-charts', siteId, range[0], range[1]],
    queryFn: async () =>
      (
        await api.get<DashboardCharts>('/dashboard/charts', {
          params: { siteId, from: range[0], to: range[1] },
        })
      ).data,
    enabled: !!siteId,
  });

  const saveSetting = useMutation({
    mutationFn: async (next: DashboardSetting) => api.put('/dashboard/settings', next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard-setting'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard-charts'] });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const enabled = setting.data?.enabledLogTypes ?? [...LOG_TYPES];

  /** 안내 문구에 쓸 사업장 이름. 이름이 겹치면 코드까지 붙는다. */
  const siteName =
    siteOptions(sites.data ?? []).find((o) => o.value === siteId)?.label ?? '이 사업장';

  /** 한 유형을 켜거나 끈다. 마지막 하나는 끌 수 없다 — 빈 대시보드가 된다. */
  const toggleType = (type: LogType, on: boolean) => {
    if (!setting.data) return;
    const next = on
      ? LOG_TYPES.filter((t) => t === type || enabled.includes(t))
      : enabled.filter((t) => t !== type);
    if (next.length === 0) {
      message.warning('일지를 한 가지 이상 켜 두어야 합니다.');
      return;
    }
    saveSetting.mutate({ ...setting.data, enabledLogTypes: next });
  };

  /* ---- 이번 달 요약 ---- */
  const summary = useMemo(() => {
    const days = calendar.data?.days ?? [];
    const written = days.reduce((n, d) => n + d.logs.length, 0);
    const confirmed = days.reduce(
      (n, d) => n + d.logs.filter((l) => l.status === 'CONFIRMED').length,
      0,
    );
    const missing = days.reduce((n, d) => n + d.missing.length, 0);
    return { written, confirmed, missing };
  }, [calendar.data]);

  const manageHint = canManage ? undefined : '사업장 관리 권한이 있는 사람만 바꿀 수 있습니다';

  // 달력·차트 제목에 붙일 사업장 이름. 선택 목록과 같은 이름(겹치면 코드 포함)을 쓴다.
  const siteLabel = siteOptions(sites.data).find((o) => o.value === siteId)?.label ?? null;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/*
        머리. 사업장 선택은 오른쪽 위에 둔다 — 아래의 요약·달력·차트가 모두 이
        사업장 기준이라, 화면에서 가장 먼저 눈이 가는 자리에 있어야 한다.
      */}
      <Card size="small">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <Space size={4}>
            <Title level={4} style={{ margin: 0 }}>
              대시보드
            </Title>
            <PageHelp title="대시보드 — 화면 설명">
              <HelpP>
                고른 사업장의 <b>이번 달 작성 현황</b>을 한눈에 봅니다. 가장 중요한 숫자는
                「미작성」입니다 — 법정 일지는 빠진 날이 있으면 안 됩니다.
              </HelpP>

              <HelpH>달력</HelpH>
              <HelpP>
                날짜를 누르면 그날 일지를 바로 열거나 만들 수 있습니다. 미작성인 날이
                표시되므로 월말에 몰아서 채울 때 쓰면 편합니다.
              </HelpP>

              <HelpH>「미작성」은 언제 붙나</HelpH>
              <HelpP>
                사업장을 고른 경우에만 따집니다. 여러 사업장을 섞으면 어느 곳이 빠진
                것인지 알 수 없기 때문입니다. 아래에 해당하는 날은 <b>빠진 것으로 보지
                않습니다.</b>
              </HelpP>
              <HelpList
                items={[
                  { term: '오늘 이후', desc: '아직 오지 않은 날입니다.' },
                  { term: '개설일 이전', desc: '그때는 쓸 일지가 없었습니다.' },
                  {
                    term: '폐쇄일 이후',
                    desc: '문을 닫은 뒤로는 쓸 일지가 없습니다. 사업장·시설 화면에서 폐쇄일을 넣어 두면 그날까지만 따집니다.',
                  },
                  {
                    term: '주말·공휴일',
                    desc: '기본은 따지지 않습니다. 주말에도 가동하는 사업장이라면 달력 오른쪽 위의 스위치를 켜세요.',
                  },
                  {
                    term: '꺼 둔 일지',
                    desc: '위의 「표시할 일지」에서 끈 서식은 미작성으로 세지 않습니다.',
                  },
                ]}
              />
              <HelpP>
                운영을 그만둔 사업장인데 미작성이 계속 쌓인다면{' '}
                <b>폐쇄일이 비어 있는 것</b>입니다. 사업장 정보에 폐쇄일을 넣어 주세요.
              </HelpP>

              <HelpH>AI 주간 보고서</HelpH>
              <HelpP>
                위의 단추를 누르면 <b>고른 사업장·기간의 운영일지를 AI 가 읽고</b> 작성
                현황·미확인 일지·이상 사항·다음 주 확인할 것을 정리해 줍니다. 일지에 적힌
                가동시간·농도·효율·pH·고장 내용까지 함께 봅니다.
              </HelpP>
              <HelpList
                items={[
                  { term: '기간', desc: '한 번에 최대 1개월. 기본값은 지난 7일입니다.' },
                  {
                    term: '요금',
                    desc: '1건마다 단추에 적힌 금액이 다음 청구서에 더해집니다. 안 만들면 0원이고, 만들다 실패하면 청구하지 않습니다.',
                  },
                  {
                    term: '주의',
                    desc: '법령 위반 여부를 판단해 주지 않습니다. 기준치는 사업장마다 달라서, 이상해 보이는 값은 「확인 필요」로만 짚어 줍니다.',
                  },
                ]}
              />

              <HelpH>표시할 일지</HelpH>
              <HelpP>
                기업이 어떤 일지를 쓰는지 정하는 설정입니다. 끈 일지는 달력의 미작성
                표시에서도 빠집니다. 폐수를 다루지 않는 사업장이라면 폐수 일지를 꺼 두세요.
                사업장마다 다르지 않고 기업 전체에 적용됩니다.
              </HelpP>

              <HelpH>처음이라면</HelpH>
              <HelpList
                items={[
                  { term: '1. 사업장·시설', desc: '사업장을 만들고 시설을 등록합니다.' },
                  { term: '2. 일지', desc: '메뉴에서 유형을 고르고 그날 기록을 적습니다.' },
                  { term: '3. 인쇄', desc: '목록에서 여러 건을 골라 법정 서식으로 한 번에 뽑습니다.' },
                ]}
              />
            </PageHelp>
          </Space>

          <Space size={8}>
            {/* 보고 있는 사업장 기준으로 만든다. 고른 것과 다른 것을 분석하면 안 된다. */}
            <AiReportButton siteId={siteId} />
            <Text type="secondary">사업장</Text>
            <Select
              style={{ minWidth: 260 }}
              popupMatchSelectWidth={false}
              placeholder="사업장을 고르세요"
              value={siteId}
              onChange={setSiteId}
              loading={sites.isLoading}
              options={siteOptions(sites.data)}
            />
          </Space>
        </div>

        {/*
          차트 켜고 끄기. 기업 설정이며 달력의 미작성 표시도 이것을 따른다.
          사업장마다 다르지 않다 — 기업이 어떤 일지를 쓰는지의 문제다.
        */}
        <Space wrap size={16} style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            표시할 일지
          </Text>
          {LOG_TYPES.map((type) => (
            <Tooltip key={type} title={manageHint}>
              <Space size={6}>
                <Switch
                  size="small"
                  checked={enabled.includes(type)}
                  disabled={!canManage || !setting.data}
                  loading={saveSetting.isPending}
                  onChange={(on) => toggleType(type, on)}
                />
                <span style={{ color: LOG_TYPE_COLOR[type] }}>{LOG_TYPE_ICON[type]}</span>
                <Text>{LOG_TYPE_MENU[type]}</Text>
              </Space>
            </Tooltip>
          ))}
        </Space>
      </Card>

      {!sites.isLoading && (sites.data ?? []).length === 0 ? (
        <Card>
          <Empty description="등록된 사업장이 없습니다. 사업장·시설 메뉴에서 먼저 등록해 주세요." />
        </Card>
      ) : (
        <>
          {/* 이번 달 요약 */}
          <Row gutter={16}>
            <Col xs={8}>
              <Card size="small">
                <Statistic title={`${month.format('M월')} 작성`} value={summary.written} suffix="건" />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small">
                <Statistic
                  title="확인완료"
                  value={summary.confirmed}
                  suffix="건"
                  valueStyle={{ color: '#389e0d' }}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small">
                <Statistic
                  title="미작성"
                  value={summary.missing}
                  suffix="건"
                  // 빠진 것이 있으면 눈에 띄게, 없으면 조용히.
                  valueStyle={{ color: summary.missing > 0 ? '#d46b08' : undefined }}
                />
              </Card>
            </Col>
          </Row>

          {/* 달력 */}
          <Card
            size="small"
            title={<CardTitle text="일지 달력" siteLabel={siteLabel} />}
            extra={
              <Space size={16} wrap>
                <Legend />
                <Tooltip title={manageHint}>
                  <Space size={6}>
                    <Switch
                      size="small"
                      checked={setting.data?.countNonWorkingDays ?? false}
                      disabled={!canManage || !setting.data}
                      onChange={(on) =>
                        setting.data &&
                        saveSetting.mutate({ ...setting.data, countNonWorkingDays: on })
                      }
                    />
                    <Text style={{ fontSize: 13 }}>주말·공휴일도 미작성으로 표시</Text>
                  </Space>
                </Tooltip>
              </Space>
            }
          >
            <Spin spinning={calendar.isFetching}>
              <LogCalendar
                value={month}
                data={calendar.data}
                onMonthChange={(d) => setMonth(d.startOf('month'))}
                onDayClick={setSelectedDay}
              />
            </Spin>
          </Card>

          {/* 차트 */}
          <Card
            size="small"
            title={<CardTitle text="일지 차트" siteLabel={siteLabel} />}
            extra={
              <Space wrap>
                <Segmented<Period>
                  size="small"
                  value={period}
                  onChange={setPeriod}
                  options={[
                    { value: 'month', label: '이번 달' },
                    { value: '30d', label: '최근 30일' },
                    { value: '90d', label: '최근 90일' },
                    { value: 'custom', label: '기간 지정' },
                  ]}
                />
                {period === 'custom' && (
                  <DatePicker.RangePicker
                    size="small"
                    value={custom}
                    allowClear={false}
                    disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                    onChange={(v) => {
                      if (v?.[0] && v[1]) setCustom([v[0], v[1]]);
                    }}
                  />
                )}
              </Space>
            }
          >
            <Spin spinning={charts.isFetching}>
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                {LOG_TYPES.filter((t) => enabled.includes(t)).map((type) => {
                  const typeCharts = charts.data?.byType[type];
                  /*
                   * 이 유형의 차트가 통째로 비었는가.
                   *
                   * 빈 차트만 늘어놓으면 "차트가 안 나온다" 로 읽힌다. 실제로는
                   * 고른 사업장에 그 유형의 일지가 없는 경우가 대부분이라,
                   * 무엇을 바꿔 보면 되는지까지 한 줄로 알려 준다.
                   */
                  const allEmpty =
                    !!typeCharts &&
                    typeCharts.every((c) =>
                      c.series.every((s) =>
                        c.points.every((p) => p[s] === null || p[s] === undefined),
                      ),
                    );

                  return (
                    <div key={type}>
                      <Title level={5} style={{ color: LOG_TYPE_COLOR[type] }}>
                        <Space size={6}>
                          {LOG_TYPE_ICON[type]}
                          {LOG_TYPE_MENU[type]}
                        </Space>
                      </Title>
                      {allEmpty && (
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message={`이 기간에 ${siteName} 의 ${LOG_TYPE_MENU[type]} 기록이 없습니다`}
                          description="기간을 늘리거나 오른쪽 위에서 사업장을 바꿔 보세요. 이 유형을 쓰지 않는다면 위의 스위치를 꺼 두면 됩니다."
                        />
                      )}
                      {typeCharts ? (
                        <ChartGrid charts={typeCharts} />
                      ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="불러오는 중" />
                      )}
                    </div>
                  );
                })}
              </Space>
            </Spin>
          </Card>
        </>
      )}

      <DayDialog
        day={selectedDay}
        siteId={siteId}
        enabledTypes={enabled}
        canWrite={canWrite}
        onClose={() => setSelectedDay(null)}
      />
    </Space>
  );
}

/*
  마지막으로 본 사업장. 이 브라우저에만 남는 편의 기능이라 저장이 안 되어도
  (사생활 보호 창, 저장소 차단) 대시보드는 첫 사업장으로 정상 동작해야 한다.
*/
const LAST_SITE_KEY = 'eco.dashboard.siteId';

function readLastSite(): string | undefined {
  try {
    return window.localStorage.getItem(LAST_SITE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeLastSite(siteId: string): void {
  try {
    window.localStorage.setItem(LAST_SITE_KEY, siteId);
  } catch {
    /* 기억하지 못해도 동작에는 지장이 없다. */
  }
}

/**
 * 카드 제목 + 기준 사업장.
 *
 * 달력과 차트가 어느 사업장 것인지 제목에서 바로 보인다. 사업장을 바꾸고
 * 아래로 내려왔을 때 "지금 보는 게 어디 것이지" 를 위로 올라가 확인하지 않게 한다.
 */
function CardTitle({ text, siteLabel }: { text: string; siteLabel: string | null }) {
  return (
    <Space size={8}>
      <span>{text}</span>
      {siteLabel && (
        <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
          · {siteLabel}
        </Text>
      )}
    </Space>
  );
}

/** 달력 범례. 작성완료는 테두리, 확인완료는 채운 모양, 미작성은 점선이다. */
function Legend() {
  const chip = (style: React.CSSProperties, text: string) => (
    <Space size={4}>
      <span
        style={{
          display: 'inline-block',
          width: 18,
          height: 12,
          borderRadius: 6,
          ...style,
        }}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {text}
      </Text>
    </Space>
  );

  return (
    <Space size={12}>
      {chip({ border: '1px solid #1677ff', background: '#fff' }, '작성완료')}
      {chip({ border: '1px solid #1677ff', background: '#1677ff' }, '확인완료')}
      {chip({ border: '1px dashed #d9d9d9', background: '#fafafa' }, '미작성')}
    </Space>
  );
}
